import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Step,
  Stepper,
  useMediaQuery,
} from "@mui/material";
import ChartIcon from "@mui/icons-material/BarChart";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import PropTypes from "prop-types";
import { useFormik } from "formik";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import Tooltip from "Components/Common/Tooltip";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import StepLabelField from "common/ApplicationApprovalTimelineChart/StepLabelField";
import {
  DCS_CONSUMER_BILLING_INCHARGE,
  DCS_CONSUMER_BILLING_OPERATOR,
  otherUtilitiesBillingAdmin3Roles,
  otherUtilitiesBillingAdmin4Roles,
  POWER_CONSUMER_BILLING_INCHARGE,
  POWER_CONSUMER_BILLING_OPERATOR,
} from "common/constant";
import {
  billingSubscriptionTypes,
  getOptionLabel,
  getOptionValue,
} from "common/enums";
import DispatchBillModel from "./DispatchBillModel";
import BillReviewModel from "./BillReviewModel";

import {
  useLazyBillingCalculationQuery,
  useLazyGetBillingCycleChartQuery,
} from "apis/Billing";
import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { getImageUrl, isAuthorizedUser } from "utils";

const ConsumerBillDetails = ({ t }) => {
  const { roles } = useSelector((state) => state.Login);

  const [searchParams, setSearchParams] = useSearchParams();
  const billingCycleId = searchParams.get("billingCycleId");
  const [documentToPreview, setDocumentToPreview] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [billCycleIdForChart, setBillCycleIdForChart] = useState("");
  const resetPageRef = useRef();

  const [
    getBillCalculationApprovalData,
    {
      data: { data: billCalculationApprovalTrackingData = [] } = {},
      isLoading,
      isFetching,
    },
  ] = useLazyGetBillingCycleChartQuery();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");
  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const {
    data: { data: billingCycleOptions } = {},
    isLoading: isBillingCycleOptionsLoading,
  } = useGetBillingCycleOptionsQuery();

  const [
    getBillingCalculations,
    {
      data: { data: billingCalculations } = {},
      isLoading: isBillingCalculationLoading,
      isFetching: isBillingCalculationFetching,
    },
  ] = useLazyBillingCalculationQuery();

  const [isDispatchBtnClicked, setIsDispatchBtnClicked] = useState(false);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter((option) => option.subscriptionType === subscriptionType)
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions, subscriptionType]
  );

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      billCycleId:
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "",
      consumerNumber: "",
      meterNumber: "",
    },
    onSubmit: async ({ billCycleId, consumerNumber, meterNumber }) => {
      try {
        await getBillingCalculations(billCycleId, {
          consumerNumber,
          meterNumber,
        });
        handleResetPage();
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    values: { billCycleId },
  } = formik;

  const columns = [
    { title: "Bill Cycle", field: "billCycle" },
    { title: "Consumer Number", field: "consumerNumber" },
    { title: "Meter Number", field: "meterNumber" },
    { title: "Consumer Name", field: "consumerName" },
    { title: "Tariff Name", field: "tariffName" },
    { title: "Net Payable", field: "netPayable" },
    {
      title: "Status",
      renderCell: ({ status }) =>
        getOptionLabel("billCalculationStatus", status),
    },
    {
      title: "Actions",
      renderCell: ({ url, approvedBy, revokedBy, id }) => (
        <Box minWidth="100px">
          <IconButton
            onClick={() =>
              setDocumentToPreview({
                url: getImageUrl(url),
                documentTypeName: "Bill",
                documentType: "pdf",
                documentName: "bill",
              })
            }
          >
            <Tooltip title={t("View Bill Receipt")}>
              <ViewIcon
                fontSize="small"
                color="primary"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
          {!!(approvedBy || revokedBy) && (
            <IconButton
              onClick={async () => {
                try {
                  await getBillCalculationApprovalData(id[0], {
                    subscriptionType,
                  });
                  setBillCycleIdForChart(id);
                } catch (error) {
                  console.error(error);
                }
              }}
            >
              <Tooltip title={t("Approval History Timeline Chart")}>
                <ChartIcon
                  fontSize="medium"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const clearBtnHandler = async () => {
    try {
      setFieldValue("consumerNumber", "");
      setFieldValue("meterNumber", "");
      await getBillingCalculations(billCycleId);
      handleResetPage();
    } catch (error) {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (updatedBillingCycleOptions.length) {
          await getBillingCalculations(
            updatedBillingCycleOptions.find(
              ({ status }) =>
                status !== getOptionValue("billingStatus", "Closed")
            )?.value ??
              updatedBillingCycleOptions.at(-1)?.value ??
              ""
          );
        }
      } catch (error) {}
    })();
  }, [updatedBillingCycleOptions]);

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length) {
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  return (
    <div>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      <Backdrop
        open={
          isBillingCycleOptionsLoading ||
          isBillingCalculationLoading ||
          isBillingCalculationFetching ||
          isLoading ||
          isFetching
        }
      />

      {!!billCycleIdForChart && (
        <Modal
          isOpen
          title="Approval Chart"
          maxWidth="lg"
          handleClose={() => setBillCycleIdForChart("")}
        >
          <Box sx={{ width: "100%" }}>
            <Stepper
              activeStep={billCalculationApprovalTrackingData.length}
              alternativeLabel={!isMobileDevice}
              className="mt-3"
              orientation={isMobileDevice ? "vertical" : "horizontal"}
            >
              {billCalculationApprovalTrackingData.map((step, index) => (
                <Step key={index}>
                  <StepLabelField step={step} />
                </Step>
              ))}
            </Stepper>
          </Box>
        </Modal>
      )}

      {isDispatchBtnClicked && (
        <DispatchBillModel
          handleClose={() => setIsDispatchBtnClicked(false)}
          subscriptionType={subscriptionType}
          billCycleId={billCycleId}
        />
      )}
      {!!billingCycleId && (
        <BillReviewModel
          handleClose={() => {
            searchParams.delete("billingCycleId");
            setSearchParams(searchParams);
          }}
          subscriptionType={subscriptionType}
        />
      )}
      <BreadCrumb title={t("Consumer Bills")} pageTitle={t("Billing")} />
      <Grid container columnSpacing={2} className="mb-1">
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <SelectField
            label={t("Subscription Type") + "*"}
            onChange={(_, { value }) => setSubscriptionType(value)}
            value={
              updatedBillingSubscriptionOptions.find(
                ({ value }) => value === subscriptionType
              ) ?? ""
            }
            options={updatedBillingSubscriptionOptions}
          />
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <SelectField
            label={t("Select bill cycle")}
            name="billCycleId"
            formik={formik}
            options={updatedBillingCycleOptions || []}
          />
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <InputFieldText
            label={t("Consumer Number")}
            name="consumerNumber"
            formik={formik}
            placeholder={t("Enter consumer number")}
          />
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <InputFieldText
            label={t("Meter Number")}
            name="meterNumber"
            formik={formik}
            placeholder={t("Enter meter number")}
          />
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            className="mb-2 me-2"
            onClick={handleSubmit}
            disabled={!billCycleId}
          >
            {t("Search")}
          </Button>
          <Button
            variant="outlined"
            onClick={clearBtnHandler}
            disabled={!billCycleId}
            className="mb-2 me-2"
          >
            {t("Clear")}
          </Button>
        </Grid>
        <Grid item>
          {!!billingCalculations?.length &&
            isAuthorizedUser(roles, [
              POWER_CONSUMER_BILLING_OPERATOR,
              POWER_CONSUMER_BILLING_INCHARGE,
              DCS_CONSUMER_BILLING_INCHARGE,
              DCS_CONSUMER_BILLING_OPERATOR,
              ...otherUtilitiesBillingAdmin3Roles,
              ...otherUtilitiesBillingAdmin4Roles,
            ]) && (
              <>
                {isAuthorizedUser(roles, [
                  POWER_CONSUMER_BILLING_INCHARGE,
                  DCS_CONSUMER_BILLING_INCHARGE,
                  ...otherUtilitiesBillingAdmin3Roles,
                ]) && (
                  <Button
                    onClick={() => setIsDispatchBtnClicked(true)}
                    variant="contained"
                    sx={{ fontSize: "13px" }}
                    className="mb-2 me-2"
                  >
                    {t("Dispatch")}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    searchParams.set("billingCycleId", billCycleId);
                    setSearchParams(searchParams);
                  }}
                  sx={{ fontSize: "13px" }}
                  className="mb-2 me-2"
                  variant="contained"
                >
                  {t("Review Bills")}
                </Button>
              </>
            )}
        </Grid>
      </Grid>
      <BaseTable
        data={billingCalculations}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        pageSize={10}
        displaySrNo
        maxHeight={
          sm
            ? "calc(100vh - 350px)"
            : md
            ? "calc(100vh - 300px)"
            : "calc(100vh - 250px)"
        }
      />
    </div>
  );
};

ConsumerBillDetails.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(ConsumerBillDetails);
